import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, Eyebrow, PageHero, CtaBand, Crumbs } from "../components/Shared";
import { CLASSES, IMG } from "../data/mock";

export default function Classes() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Programs", path: "/classes" },
  ];

  return (
    <>
      <Seo
        title="Programs & Classes in West Roxbury | Boston Iron Grit"
        description="Every program at Boston Iron Grit in West Roxbury: CrossFit, weightlifting, powerlifting, HYROX, personal training, nutrition coaching and 50+ fitness."
        path="/classes"
        schemas={[breadcrumbSchema(crumbs)]}
      />
      <PageHero
        label="Programs"
        title={<>Every goal has a program.<br /><span className="text-neutral-500">Every program has a coach.</span></>}
        sub="Ten distinct programs under one roof in West Roxbury — from your very first squat to competition prep. Not sure which fits? Start with a free goal review and we'll point you to the right door."
        image={IMG.interior}
        video="/video/hero-conditioning-15s"
      />

      <section className="bg-[#0A0A0B] pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
        </div>
      </section>

      <section className="bg-[#0A0A0B] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CLASSES.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 3) * 80}>
                <Link to={`/classes/${c.slug}`} className="group relative block h-[380px] overflow-hidden bg-[#121214] border border-white/5 hover:border-white/15 transition-colors">
                  <img
                    src={c.image}
                    alt={`${c.name} — coached strength and conditioning program`}
                    className="absolute inset-0 w-full h-full object-cover object-[center_25%] opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/40 to-transparent" />
                  <div className="absolute top-5 left-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white bg-[#9D0423] px-3 py-1.5">{c.heroLabel}</span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <h2 className="font-heading font-extrabold uppercase text-2xl text-white tracking-tight">{c.name}</h2>
                    <p className="mt-2 text-[13px] text-neutral-400 leading-snug">{c.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white">
                      View Program <ArrowUpRight size={13} className="text-[#9D0423] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}

            {/* HYROX card */}
            <Reveal delay={160}>
              <Link to="/hyrox" className="group relative block h-[380px] overflow-hidden border border-[#9D0423]/40 hover:border-[#9D0423] transition-colors">
                <img
                  src={IMG.hyroxRun}
                  alt="Coach Miguel coaching wall balls at Boston Iron Grit"
                  className="absolute inset-0 w-full h-full object-cover object-[center_25%] opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/40 to-transparent" />
                <div className="absolute top-5 left-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white bg-[#9D0423] px-3 py-1.5">Race Training</span>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h2 className="font-heading font-extrabold uppercase text-2xl text-white tracking-tight">HYROX <span className="text-[#9D0423]">Boston</span></h2>
                  <p className="mt-2 text-[13px] text-neutral-400 leading-snug">Train for the world's fastest-growing fitness race. 8 runs, 8 stations, dedicated club sessions.</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white">
                    Enter the Race <ArrowRight size={13} className="text-[#9D0423] group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Adaptive training */}
      <section className="bg-[#0A0A0B] pb-16 lg:pb-20" data-testid="adaptive-training-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="grid md:grid-cols-[auto_1fr] gap-8 items-center bg-[#121214] border border-white/8 p-8 lg:p-10">
              <Link to="/coaches/cass-eddy-stewart" className="group block w-full md:w-[420px] shrink-0" data-testid="adaptive-coach-photo-link">
                <img
                  src="/images/coaches/cass-rowing.jpg"
                  alt="Coach Cassandra Eddy-Stewart racing her single scull as an adaptive rower"
                  className="w-full aspect-[2/1] object-cover border border-white/10 group-hover:border-[#9D0423]/60 transition-colors"
                  loading="lazy"
                />
              </Link>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423]">// Every Athlete Belongs</p>
                <h2 className="mt-3 font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight">Adaptive Training.</h2>
                <p className="mt-4 text-[15px] text-neutral-300 leading-relaxed max-w-2xl">
                  Training around an injury, disability or limited mobility? Coach Cassandra Eddy-Stewart — our Adaptive Coach
                  Specialist, and an adaptive athlete herself competing in HYROX, rowing and CrossFit — adapts every workout so you
                  can train hard at our West Roxbury gym. She works with adaptive athletes by appointment — start with a free goal review.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/get-started"
                    data-testid="adaptive-goal-review-cta"
                    className="inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-6 py-3.5 transition-colors"
                  >
                    Book a Free Goal Review <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/coaches/cass-eddy-stewart"
                    data-testid="adaptive-coach-bio-link"
                    className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/60 text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-6 py-3.5 transition-colors"
                  >
                    Meet Coach Cass <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Helper strip */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-16">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Book a goal review", d: "A free 20-minute conversation about your goals, history and limitations. No workout, no pressure." },
            { n: "02", t: "Learn the fundamentals", d: "Three private sessions covering the essential movements — so class day one feels familiar, not terrifying." },
            { n: "03", t: "Join the community", d: "Pick your schedule, meet the 5:15 AM crew or the noon regulars, and start stacking wins." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 100}>
              <div className="relative p-7 bg-[#121214] border border-white/5 h-full">
                <div className="font-heading font-extrabold text-5xl text-white/10">{s.n}</div>
                <h3 className="mt-3 font-heading font-bold uppercase text-xl text-white tracking-tight">{s.t}</h3>
                <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
